import { h } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { currencyRate } from "./monogram-cart.js";
import MonogramAccordion from "./monogram-accordion.jsx";
import MonogramPreview from "./monogram-preview.jsx";
import {
  ColorStep,
  FontStep,
  PlacementStep,
  SizeStep,
  TextStep,
  TypeSelector,
} from "./monogram-steps.jsx";
import {
  GOLD_FOIL,
  HAND_PAINTED,
  HAND_PAINTED_ADDITIONAL_LETTER_PRICE,
  HAND_PAINTED_FIRST_LETTER_PRICE,
  availableTypes,
  fontFor,
  fontsFor,
  goldFoilPrice,
  monogramPrice,
  monogramText,
  normalizeSelection,
  placementFor,
  placementsFor,
  pricingVariant,
  textRules,
  visibleSteps,
} from "./monogram-options.js";

const STEP_TITLES = {
  text: "step_text",
  font: "step_font",
  size: "step_size",
  color: "step_color",
  placement: "step_placement",
};

/**
 * Price labels for the text step, in the shopper's currency.
 *
 * @param {Object} config
 * @param {Object} [variant] - The product variant being monogrammed
 */
function priceLabels(config, variant) {
  const label = (type, price) =>
    pricingVariant(config, type, price)?.priceLabel ?? null;

  return {
    goldFoil: variant
      ? label(GOLD_FOIL, goldFoilPrice(variant.price, currencyRate()))
      : null,
    firstLetter: label(HAND_PAINTED, HAND_PAINTED_FIRST_LETTER_PRICE),
    additionalLetter: label(HAND_PAINTED, HAND_PAINTED_ADDITIONAL_LETTER_PRICE),
  };
}

/**
 * @param {Object} props
 * @param {Object} [props.variant] - The product variant being monogrammed,
 *   which sets gold foil's price; the product's first when absent
 * @param {Function} props.onAdd - Called with the selection; returns a
 *   promise that rejects with the cart's message when the add fails
 * @param {Function} [props.onSelectionChange] - Called with each new selection
 * @param {Object} [props.previewProps] - Passed through to MonogramPreview
 */
export default function MonogramConfigurator({
  config,
  variant = config.variants?.[0],
  initialSelection,
  onAdd,
  onSelectionChange,
  previewProps,
}) {
  const { strings } = config;
  const prices = useMemo(() => priceLabels(config, variant), [config, variant]);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  const types = useMemo(() => availableTypes(config), [config]);
  const [rawSelection, setSelection] = useState(initialSelection);
  // Normalised against the current config rather than only on change, so a
  // config edited from outside (the configure view) can't leave it stale.
  const selection = useMemo(
    () => normalizeSelection(config, rawSelection),
    [config, rawSelection],
  );

  useEffect(() => {
    onSelectionChange?.(selection);
  }, [selection]);
  const [openSteps, setOpenSteps] = useState(() => new Set(["text"]));

  const update = (changes) => setSelection({ ...selection, ...changes });

  const toggleStep = (step) =>
    setOpenSteps((current) => {
      const next = new Set(current);
      if (!next.delete(step)) next.add(step);
      return next;
    });

  const font = fontFor(config, selection);
  const placement = placementFor(config, selection);
  const rules = textRules(config, selection);
  const text = monogramText(selection);
  const priceLabel = variant
    ? pricingVariant(
        config,
        selection.type,
        monogramPrice(selection, variant.price, currencyRate()),
      )?.priceLabel
    : null;
  const notes =
    selection.type === GOLD_FOIL
      ? config.goldFoil?.notes
      : config.handPainted?.notes;

  const add = async () => {
    setAdding(true);
    setError(null);

    try {
      await onAdd(selection);
    } catch (addError) {
      setError(
        addError?.message || window.theme.translations(strings, "add_error"),
      );
    } finally {
      setAdding(false);
    }
  };

  const renderStep = (step) => {
    switch (step) {
      case "text":
        return (
          <TextStep
            config={config}
            selection={selection}
            rules={rules}
            font={font}
            prices={prices}
            strings={strings}
            onChange={update}
          />
        );
      case "font":
        return (
          <FontStep
            fonts={fontsFor(config, selection)}
            selection={selection}
            onChange={update}
          />
        );
      case "size":
        return (
          <SizeStep
            placement={placement}
            selection={selection}
            strings={strings}
            onChange={update}
          />
        );
      case "color":
        return (
          <ColorStep
            config={config}
            selection={selection}
            strings={strings}
            onChange={update}
          />
        );
      case "placement":
        return (
          <PlacementStep
            placements={placementsFor(config, selection.type)}
            selection={selection}
            onChange={update}
          />
        );
    }
  };

  return (
    <div class="monogram">
      <div class="monogram__preview">
        <MonogramPreview
          config={config}
          selection={selection}
          text={text}
          font={font}
          placement={placement}
          strings={strings}
          {...previewProps}
        />
      </div>

      <div class="monogram__options">
        <TypeSelector
          types={types}
          selection={selection}
          strings={strings}
          onChange={update}
        />

        <div class="monogram__steps">
          {visibleSteps(config, selection.type).map((step, index) => (
            <MonogramAccordion
              key={step}
              id={`Monogram-${config.productId}-${step}`}
              number={index + 1}
              title={window.theme.translations(strings, STEP_TITLES[step])}
              open={openSteps.has(step)}
              onToggle={() => toggleStep(step)}
            >
              {renderStep(step)}
            </MonogramAccordion>
          ))}
        </div>

        <button
          type="button"
          class="button button--black button--full monogram__add"
          disabled={!text || adding}
          aria-busy={adding ? "true" : undefined}
          onClick={add}
        >
          {priceLabel
            ? window.theme.translations(strings, "add_monogram_price", {
                price: priceLabel,
              })
            : window.theme.translations(strings, "add_monogram")}
        </button>

        {error && (
          <p class="monogram__error body-small" role="alert">
            {error}
          </p>
        )}

        {notes && (
          <div
            class="monogram__notes body-small rte--simple"
            dangerouslySetInnerHTML={{ __html: notes }}
          />
        )}
      </div>
    </div>
  );
}
